import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useSEO, createOrganizationSchema, createWebPageSchema } from '../../utils/seo';

export default function ContactPage() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useSEO({
    title: 'İletişim - AXIOM Kalite Kontrol ve İnsan Kaynakları Danışmanlığı',
    description: 'AXIOM ile iletişime geçin. Kalite kontrol ve insan kaynakları danışmanlığı hizmetlerimiz hakkında bilgi alın.',
    keywords: 'iletişim, contact, axiom, kalite kontrol, insan kaynakları, danışmanlık',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
    schema: {
      '@graph': [
        createOrganizationSchema(),
        createWebPageSchema(
          'İletişim - AXIOM',
          'AXIOM ile iletişime geçin. Kalite kontrol ve insan kaynakları danışmanlığı hizmetlerimiz hakkında bilgi alın.'
        )
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.message.trim()) {
      setFormStatus('error');
      setFormMessage(t('form_validation_required'));
      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 3000);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus('error');
      setFormMessage(t('form_validation_email'));
      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 3000);
      return;
    }

    if (formData.message.length > 500) {
      setFormStatus('error');
      setFormMessage(t('form_validation_length'));
      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 3000);
      return;
    }

    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d544ehvb58v6mebe8jmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormMessage(t('form_success'));
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
        setTimeout(() => {
          setFormStatus('idle');
          setFormMessage('');
        }, 5000);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setFormStatus('error');
      setFormMessage(t('form_error'));
      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header>
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <img src="https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/edcc2fca5481151c158722794d786f43.png" alt="AXIOM Logo" className="h-12 w-12 object-contain" />
                <h1 className="text-xl font-bold text-gray-900">AXIOM</h1>
              </Link>
              
              {/* Desktop Menu */}
              <div className="hidden md:flex items-center gap-8">
                <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_home')}</Link>
                <Link to="/about" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_about')}</Link>
                <Link to="/services" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_services')}</Link>
                <Link to="/training" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_training')}</Link>
                <Link to="/blog" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_blog')}</Link>
                <Link to="/career" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">Kariyer</Link>
                <Link to="/contact" className="bg-teal-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">{t('nav_contact')}</Link>
                <LanguageSwitcher />
              </div>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
              <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
                <div className="flex flex-col gap-2">
                  <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_home')}</Link>
                  <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_about')}</Link>
                  <Link to="/services" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_services')}</Link>
                  <Link to="/training" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_training')}</Link>
                  <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_blog')}</Link>
                  <Link to="/career" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">Kariyer</Link>
                  <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="bg-teal-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors text-center whitespace-nowrap cursor-pointer">{t('nav_contact')}</Link>
                  <div className="px-3 py-2">
                    <LanguageSwitcher />
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 bg-gradient-to-br from-teal-50 via-white to-emerald-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-teal-100 rounded-full px-4 py-2 mb-6">
                <i className="ri-mail-line text-teal-600 text-sm"></i>
                <span className="text-teal-700 text-sm font-semibold">İletişim</span>
              </div>
              <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                {t('contact_title')}
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {t('contact_subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-8">Bize Ulaşın</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('contact_email')}</h4>
                      <p className="text-gray-600">info@axiomkalite.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('contact_phone')}</h4>
                      <p className="text-gray-600">+90 (555) 820 46 46</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('contact_address')}</h4>
                      <p className="text-gray-600">Maslak Mahallesi, Büyükdere Caddesi<br /> İstanbul</p>
                    </div>
                  </div>
                </div>

                <div className="mt-12">
                  <h4 className="font-semibold text-gray-900 mb-4">Sosyal Medya</h4>
                  <div className="flex gap-4">
                    <a href="https://www.linkedin.com/in/axiom-kalite" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill text-xl"></i>
                    </a>
                    <a href="https://x.com/AxiomKalite" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill text-xl"></i>
                    </a>
                    <a href="https://www.instagram.com/axiomkalite/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-instagram-fill text-xl"></i>
                    </a>
                    <a href="https://www.facebook.com/profile.php?id=61585433974202" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-facebook-fill text-xl"></i>
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Mesaj Gönderin</h3>
                <form 
                  id="contact-form" 
                  data-readdy-form 
                  className="space-y-6" 
                  onSubmit={handleFormSubmit}
                >
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">{t('form_name')}</label>
                    <input 
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                      placeholder={t('form_name_placeholder')}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">{t('form_email')}</label>
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                      placeholder={t('form_email_placeholder')}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">{t('form_phone')}</label>
                    <input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                      placeholder={t('form_phone_placeholder')}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">{t('form_message')}</label>
                    <textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleFormChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all resize-none text-sm"
                      placeholder={t('form_message_placeholder')}
                      required
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 karakter</p>
                  </div>
                  
                  {formMessage && (
                    <div className={`p-4 rounded-lg ${
                      formStatus === 'success' 
                        ? 'bg-green-50 text-green-800 border border-green-200' 
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}>
                      <p className="text-sm">{formMessage}</p>
                    </div>
                  )}
                  
                  <button 
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className={`w-full py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                      formStatus === 'submitting'
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                  >
                    {formStatus === 'submitting' ? t('form_submitting') : t('form_submit')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Ofisimizi Ziyaret Edin</h2>
              <p className="text-lg text-gray-600">Maslak Mahallesi, Büyükdere Caddesi, İstanbul</p>
            </div>
            <div className="rounded-3xl overflow-hidden h-96 shadow-lg">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3007.8!2d29.0!3d41.1!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDA2JzAwLjAiTiAyOcKwMDAnMDAuMCJF!5e0!3m2!1str!2str!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <img src="https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/edcc2fca5481151c158722794d786f43.png" alt="AXIOM Logo" className="h-10 w-10 object-contain" />
                <span className="text-2xl font-bold">AXIOM</span>
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                {t('footer_description')}
              </p>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/axiom-kalite" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="https://x.com/AxiomKalite" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-lg"></i>
                </a>
                <a href="https://www.instagram.com/axiomkalite/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
                <a href="https://www.facebook.com/profile.php?id=61585433974202" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_services')}</h4>
              <ul className="space-y-3">
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_quality')}</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_hr')}</Link></li>
                <li><Link to="/training" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_training')}</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_audit')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_corporate')}</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_about')}</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_team')}</a></li>
                <li><Link to="/career" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_career')}</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_contact')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_resources')}</h4>
              <ul className="space-y-3">
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_blog')}</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_references')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_faq')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_documents')}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
              <p>{t('footer_copyright')}</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                {t('footer_powered')}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}